import { useState, useEffect } from "react";
import type { Variation, Bucket } from "../../types/experiment";
import { ExperimentType } from "../../types/experiment";
import { BucketEditor } from "./BucketEditor";
import { EnumSelect } from "../Shared/EnumSelect";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Trash2, Plus } from "lucide-react";

interface VariationEditorProps {
  variation: Variation;
  index: number;
  onChange: (variation: Variation) => void;
  onRemove: () => void;
}

const experimentTypeOptions = Object.entries(ExperimentType)
  .filter(([k]) => isNaN(Number(k)))
  .map(([key, value]) => ({ value: value as number, label: key }));

export function VariationEditor({ variation, index, onChange, onRemove }: VariationEditorProps) {
  const [idStr, setIdStr] = useState(String(variation.id));

  useEffect(() => {
    setIdStr(String(variation.id));
  }, [variation.id]);

  const updateBucket = (bucketIndex: number, bucket: Bucket) => {
    const buckets = [...variation.buckets];
    buckets[bucketIndex] = bucket;
    onChange({ ...variation, buckets });
  };

  const removeBucket = (bucketIndex: number) => {
    onChange({ ...variation, buckets: variation.buckets.filter((_, i) => i !== bucketIndex) });
  };

  const addBucket = () => {
    onChange({
      ...variation,
      buckets: [
        ...variation.buckets,
        { start: 0, stop: 100, type: ExperimentType.UNSPECIFIED },
      ],
    });
  };

  const totalAlloc = variation.buckets.reduce(
    (sum, b) => sum + Math.max(0, b.stop - b.start),
    0,
  );

  return (
    <div className="border-t border-surface1 pt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-mauve">V{index + 1}</span>
          <span className="text-sm text-subtext1">
            {variation.label || `Variation ${index + 1}`}
          </span>
          <span className="text-[10px] text-overlay0 bg-surface0 px-1.5 py-0.5 rounded">
            {variation.target_allocation}%
          </span>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="h-7 w-7">
          <Trash2 className="h-3.5 w-3.5 text-overlay1 hover:text-red" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs mb-1.5 block text-overlay1">ID</Label>
          <Input
            type="number"
            value={idStr}
            onChange={(e) => {
              setIdStr(e.target.value);
            }}
            onBlur={() => {
              const n = Number(idStr);
              if (!isNaN(n)) {
                onChange({ ...variation, id: n });
              } else {
                setIdStr(String(variation.id));
              }
            }}
          />
        </div>
        <div>
          <Label className="text-xs mb-1.5 block text-overlay1">Label</Label>
          <Input
            value={variation.label}
            onChange={(e) => onChange({ ...variation, label: e.target.value })}
            placeholder="e.g. Control, Variant A"
          />
        </div>
        <div>
          <Label className="text-xs mb-1.5 block text-overlay1">Target Allocation</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={variation.target_allocation}
              onChange={(e) =>
                onChange({ ...variation, target_allocation: parseFloat(e.target.value) || 0 })
              }
              className="flex-1"
            />
            <span className="text-overlay1 text-xs">%</span>
          </div>
        </div>
        <EnumSelect
          label="Type"
          value={variation.type}
          options={experimentTypeOptions}
          onChange={(value) => onChange({ ...variation, type: value })}
        />
      </div>

      <div className="mt-3">
        <Label className="text-xs mb-1.5 block text-overlay1">Owning Experiment ID</Label>
        <Input
          value={variation.owning_experiment_id}
          onChange={(e) => onChange({ ...variation, owning_experiment_id: e.target.value })}
          placeholder="experiment_id"
        />
      </div>

      <div className="mt-3">
        <Label className="text-xs mb-1.5 block text-overlay1">Configuration</Label>
        <Textarea
          value={variation.configuration ?? ""}
          onChange={(e) => onChange({ ...variation, configuration: e.target.value || undefined })}
          placeholder="Optional JSON configuration"
          className="min-h-[48px] text-xs"
        />
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-overlay1">
            Buckets ({variation.buckets.length})
            <span className="ml-2 text-overlay0">total range: {totalAlloc}%</span>
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={addBucket} className="h-7 text-xs">
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Button>
        </div>

        {variation.buckets.length > 0 && (
          <div className="h-2 bg-surface1 rounded-full overflow-hidden flex mb-3">
            {variation.buckets.map((bucket, bi) => {
              const w = Math.max(0, bucket.stop - bucket.start);
              return (
                <div
                  key={bi}
                  className="h-full bg-mauve/80 first:rounded-l-full last:rounded-r-full"
                  style={{ width: `${w}%` }}
                  title={`Bucket ${bi + 1}: [${bucket.start} - ${bucket.stop})`}
                />
              );
            })}
          </div>
        )}

        {variation.buckets.length > 0 && (
          <div className="text-[10px] text-overlay0 grid grid-cols-[24px_80px_80px_1fr_28px] gap-3 px-1 mb-1">
            <span></span>
            <span>Start</span>
            <span>Stop</span>
            <span>Type</span>
            <span></span>
          </div>
        )}

        <div>
          {variation.buckets.map((bucket, bi) => (
            <BucketEditor
              key={bi}
              bucket={bucket}
              index={bi}
              onChange={(b) => updateBucket(bi, b)}
              onRemove={() => removeBucket(bi)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
